import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export function Tooltip({ children, content, position = 'top' }) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      setTooltipPosition({
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [isVisible]);

  if (!content) {
    return children;
  }

  const tooltipContent = isVisible && tooltipPosition.top > 0 && createPortal(
    <div
      style={{
        position: 'absolute',
        zIndex: 10000,
        top: position === 'top'
          ? tooltipPosition.top - 8
          : tooltipPosition.top + tooltipPosition.height + 8,
        left: tooltipPosition.left + tooltipPosition.width / 2,
        transform: position === 'top'
          ? 'translate(-50%, -100%)'
          : 'translate(-50%, 0)',
        backgroundColor: 'var(--lodge-white)',
        color: 'var(--lodge-onyx-900)',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '13px',
        lineHeight: '1.6',
        minWidth: '200px',
        boxShadow: 'var(--shadow-lg)',
        border: '1px solid var(--lodge-onyx-100)',
        pointerEvents: 'none',
      }}
    >
      {content}
      {/* Arrow */}
      <div
        style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderStyle: 'solid',
          ...(position === 'top' && {
            bottom: '-7px',
            left: '50%',
            transform: 'translateX(-50%)',
            borderWidth: '7px 7px 0 7px',
            borderColor: 'var(--lodge-white) transparent transparent transparent',
          }),
          ...(position === 'bottom' && {
            top: '-7px',
            left: '50%',
            transform: 'translateX(-50%)',
            borderWidth: '0 7px 7px 7px',
            borderColor: 'transparent transparent var(--lodge-white) transparent',
          }),
        }}
      />
      {/* Arrow border (for the outline effect) */}
      <div
        style={{
          position: 'absolute',
          width: '0',
          height: '0',
          borderStyle: 'solid',
          ...(position === 'top' && {
            bottom: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            borderWidth: '8px 8px 0 8px',
            borderColor: 'var(--lodge-onyx-100) transparent transparent transparent',
          }),
          ...(position === 'bottom' && {
            top: '-8px',
            left: '50%',
            transform: 'translateX(-50%)',
            borderWidth: '0 8px 8px 8px',
            borderColor: 'transparent transparent var(--lodge-onyx-100) transparent',
          }),
        }}
      />
    </div>,
    document.body
  );

  return (
    <div
      ref={triggerRef}
      style={{ position: 'relative', display: 'inline-block', width: '100%', height: '100%' }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {tooltipContent}
    </div>
  );
}
